import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, ClipboardList, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink,
} from 'lucide-react';

const SLUG = 'snaptrude';
const SHARE_EMAIL = 'suresh@socialseo.in';
const NOTION_CONTENT_OS = 'https://www.notion.so/3d53ac2a530981cb97b7c2c27c2172fc';

const QUESTIONNAIRE_URL = 'https://docs.google.com/document/d/11J0XCJC6A-P4EQopBeS5XfBxDi14Q8qiMO8XC2mM4cE/edit';
const DRIVE_EXAMPLE_URL = 'https://drive.google.com/drive/folders/1sh4_G0gAFi9UO2rcarQk1JcNCrfkt8L9?usp=sharing';

const data = {
    client: 'Uzma',
    brandLabel: 'Snaptrude',
    pageTitle: 'Onboarding | Snaptrude via SocialSEO',
    badge: 'Ready to Launch',
    headline: 'Welcome to the Machine.',

    greeting: 'Hey Uzma,',
    intro: [
        'Great to have you in. This page is the single home base for getting the Snaptrude account army started: your founder account plus the three avatar accounts. Everything you need to do in the first few days is laid out below as a short checklist.',
        'A quick note on how the engagement runs. The first 7 days are onboarding: we lock the positioning for all four accounts, design the three avatar personas for your approval, train the founder clone, and deploy your content engine. The heavy setup happens up front so production runs like clockwork after.',
        'Work down the steps below. The agreement is signed. Once the invoice is cleared, we move straight into production.',
    ],

    steps: [
        {
            icon: FileSignature,
            done: true,
            title: 'Agreement signed',
            desc: 'Signed by both sides on 24 September 2026. Your copy is here whenever you need it. The KPIs get added as a signed addendum before the first post goes live, once your questionnaire and baselines are in.',
            links: [
                { label: 'Signed Service Agreement', href: `/assets/${SLUG}/agreement.pdf`, icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear your first invoice',
            desc: 'Month 1, USD 3,200 invoiced in INR at the day\'s rate, due 2 October 2026. Payable by bank transfer. Our full account details are printed on the invoice itself. If you wire internationally, please set the transfer to the "OUR" charge code so the correct amount reaches us.',
            links: [
                { label: 'Download Invoice SSEO03425092026 (bank details inside)', href: `/assets/${SLUG}/invoice.pdf`, icon: Download },
            ],
        },
        {
            icon: ClipboardList,
            title: 'Fill your Founder Questionnaire',
            desc: 'This is how we lock the positioning, voice and content pillars for the founder account, and it feeds the avatar personas too. This is your own copy, just open it and fill it in. It takes about 20 minutes, and the sharper the answers, the sharper the content.',
            links: [
                { label: 'Open your Questionnaire', href: QUESTIONNAIRE_URL, icon: ExternalLink },
            ],
        },
        {
            icon: Video,
            title: 'Send the founder training data',
            desc: `This is what we build the founder clone from. Set up one Drive folder organized like the example below, then share it with us at ${SHARE_EMAIL}. No professional setup needed: a phone camera in good natural light with clean audio is exactly what the clone learns best from.`,
            links: [
                { label: 'See the Example Folder', href: DRIVE_EXAMPLE_URL, icon: ExternalLink },
            ],
            tips: [
                'Audio: up to 2 hours of clear, solo audio of the founder speaking. A quiet room matters more than the microphone.',
                'Video: at least 3 minutes on camera across a few different outfits and, where possible, different settings. Face the light, look into the lens, talk naturally.',
                'Portraits: a set of clean portrait photos we use for thumbnails and covers.',
                'B-roll (optional): a separate folder for office clips, product moments, or any extra footage.',
                'The three avatar accounts need nothing from you here: we design those personas from scratch and you approve them before anything goes live.',
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: scripts pipeline, posting calendar for all four accounts, and approvals. This is where you give inputs and sign off on content.',
            links: [
                { label: 'Open your Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
    ],

    timelineTitle: 'What the engagement looks like',
    phases: [
        {
            title: 'Onboarding & Setup',
            window: 'Days 1–7',
            items: [
                'Agreement signed, first invoice cleared, questionnaire in.',
                'Three avatar personas designed and approved by you; founder clone trained and pinned.',
                'Content engine deployed, positioning locked, first scripts approved.',
            ],
        },
        {
            title: 'Launch',
            window: 'Week 2',
            items: [
                'All four accounts go live and start posting.',
                'Hook variants calibrated per account against real data.',
            ],
        },
        {
            title: 'Month 1 Production',
            window: 'Weeks 2–5',
            items: [
                'Daily cadence ships across the account army.',
                'Best-performing personas and formats identified; volume follows the winners.',
            ],
        },
        {
            title: 'Compound',
            window: 'Months 2–3',
            items: [
                'Four accounts cross-pollinate: winning hooks and topics move between pages.',
                'The back catalogue compounds and the network effect kicks in.',
                'Monthly review with you on numbers and direction.',
            ],
        },
    ],
    timelineNote:
        'Onboarding is the heavy-lift week. Once the personas are approved and the clones are pinned, production runs like clockwork and turnaround tightens significantly.',

    closing: {
        line1: 'Any questions on the agreement, the questionnaire, or the recording setup, just reply on our email thread.',
        line2: 'Knock out the checklist and we get the infrastructure live this week!',
    },
};

export default function SnaptrudeOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                                const StepIcon = step.icon;
                                return (
                                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                        <div className="flex items-start gap-4">
                                            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-bold relative ${step.done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                                                <StepIcon className="w-5 h-5" />
                                                <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14] ${step.done ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{step.done ? '✓' : i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">{step.title}{step.done && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>}</h3>
                                                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                                {step.tips && (
                                                    <ul className="mb-4 space-y-2">
                                                        {step.tips.map((tip, t) => (
                                                            <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="flex flex-wrap gap-3">
                                                    {step.links.map((link, l) => {
                                                        const LinkIcon = link.icon || ExternalLink;
                                                        return (
                                                            <a
                                                                key={l}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                            >
                                                                <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
